import CircularProgress from '@mui/material/CircularProgress';
import { lazy, Suspense } from 'react';
import { type RouteObject } from 'react-router';

import ContextualRoute from './ContextualRoute';
import { paths } from './paths';
import { type RootRouteMode } from './reactiveRoutes';
import RouteErrorBoundary from './RouteErrorBoundary';

import FunnelContextProvider from '@/Contexts/Funnel/Provider';
import SchedulerV2Provider from '@/Contexts/Scheduler/Provider';
import OnboardingPage from '@/pages/OnboardingPage';
import PageNotFound from '@/pages/PageNotFound';
import ProjectInvitationAcceptancePage from '@/pages/ProjectInvitationAcceptancePage';
// Both are imported eagerly on purpose: a failed chunk fetch is one of the
// errors they exist to handle, so neither can afford to be code-split.

const ForgotPasswordPage = lazy(() => import('@/pages/Auth/ForgotPasswordPage'));
const LoginPage = lazy(() => import('@/pages/Auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/Auth/RegisterPage'));
const ResetPasswordPage = lazy(() => import('@/pages/Auth/ResetPassword'));
const ConflictsPage = lazy(() => import('@/pages/ConflictsPage'));
const FunnelPage = lazy(() => import('@/pages/FunnelPage'));
const ManagerPage = lazy(() => import('@/pages/ManagerPage'));
const ReportPage = lazy(() => import('@/pages/ReportPageV2'));
const SchedulerPage = lazy(() => import('@/pages/SchedulerPage'));
const SuperPage = lazy(() => import('@/pages/SuperPage'));
const ReportContextProvider = lazy(() => import('@/Contexts/ReportContext/Provider'));

const withSuspense = (element: React.ReactNode) => <Suspense fallback={<CircularProgress />}>{element}</Suspense>;

const schedulerPageWithContext = () =>
  withSuspense(
    <SchedulerV2Provider>
      <SchedulerPage />
    </SchedulerV2Provider>,
  );

const createCommonRoutes = (): RouteObject[] => [
  { path: paths.login, element: withSuspense(<LoginPage />) },
  { path: paths.register, element: withSuspense(<RegisterPage />) },
  { path: paths.forgotPassword, element: withSuspense(<ForgotPasswordPage />) },
  { path: paths.resetPassword, element: withSuspense(<ResetPasswordPage />) },
  { path: paths.projectInvitationAcceptance, element: withSuspense(<ProjectInvitationAcceptancePage />) },
];

const createModeRoutes = (rootElement: React.ReactNode, rootChildren: RouteObject[] = []): RouteObject[] => [
  {
    // Pathless wrapper so every route below inherits one error element,
    // rather than each declaring its own.
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <ContextualRoute />,
        children: [
          { path: paths.root, element: rootElement },
          ...rootChildren,
          { path: paths.scheduler, element: schedulerPageWithContext() },
          { path: paths.manager, element: withSuspense(<ManagerPage />) },
          { path: paths.onboarding, element: withSuspense(<OnboardingPage />) },
        ],
      },
      ...createCommonRoutes(),
      { path: '*', element: <PageNotFound /> },
    ],
  },
];

export const routesByMode: Record<RootRouteMode, RouteObject[]> = {
  manager: createModeRoutes(withSuspense(<ManagerPage />), []),
  scheduler: createModeRoutes(schedulerPageWithContext(), [
    { path: paths.conflicts, element: withSuspense(<ConflictsPage />) },
    { path: paths.singleProject, element: schedulerPageWithContext() },
  ]),
  report: createModeRoutes(
    withSuspense(
      <ReportContextProvider>
        <ReportPage />
      </ReportContextProvider>,
    ),
    [],
  ),
  funnel: createModeRoutes(
    withSuspense(
      <FunnelContextProvider>
        <FunnelPage />
      </FunnelContextProvider>,
    ),
    [],
  ),
  super: createModeRoutes(withSuspense(<SuperPage />), []),
};
